import React, { useCallback, useEffect, useState } from "react";
import { connect, useDispatch } from "react-redux";
import Dropzone from "react-dropzone";
import { Box, CircularProgress, Paper, Typography } from "@mui/material";
import styles from "./ImportPDF.styles";
import AlertService from "../../common/services/notification.service";
import { useNavigate } from "react-router-dom";
import { savePDFRequest, sendPDFRequest, unlinkPDFRequest } from "./actions";
import { selectPDF } from "./selectors";
import Head from "./components/Head";
import Footer from "./components/Footer";
import Main from "./components/Main";
import dayjs, { Dayjs } from 'dayjs';
import { modelTypes } from "../../common/constants/field-constants";
import type{ RootState } from '../../reducers';

export type HandleChangeEvent =
  | string
  | boolean
  | Dayjs
  | null;

const pdfInitialState = {
  fullList: false,
  lastImportDate: dayjs(new Date()) as Dayjs | null,
  type: "",
  isDead: 3,
  open: false,
  noTypeWarning: false,
  statistic: [],
  entity: {
    entity_type: "",
    is_dead: null as boolean | null,
  },
  object: {},
  linkedObjects: [],
  objectsForLink: [],
  objectsForUnlink: [],
  objectsForSaveAndLink: [],
  objectsForCheckAndUpdate: [],
  pdfData: {},
  fileForDriveImport: {},
  unitsForSave: [],
  nonResidentOwnersInDB: [],
  nonResidentOwnersNotInDB: [],
  residentOwnersInDB: [],
  residentOwnersNotInDB: [],
};

const ImportPDF = (props: any) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [pdfState, setPdfState] = useState(pdfInitialState);
  const [notType, setNotType] = useState(false);
  const { isFetching, pdfData, parsedPDFData, fileForDriveImport } = props;

  useEffect(() => {
    setPdfState({
      fullList: false,
      lastImportDate: dayjs(new Date()),
      type: "",
      isDead:
        parsedPDFData?.entity?.is_dead === true
          ? 2
          : parsedPDFData?.entity?.is_dead === false
            ? 1 : 3,
      open: false,
      noTypeWarning: false,
      pdfData: pdfData,
      statistic: parsedPDFData.statistic,
      entity: parsedPDFData.entity,
      object: parsedPDFData.object,
      linkedObjects: parsedPDFData.linkedObjects || [],
      objectsForLink: parsedPDFData.objectsForLink || [],
      // objectsForUnlink: parsedPDFData.objectsForUnlink || [],
      objectsForUnlink: [],
      objectsForSaveAndLink: parsedPDFData.objectsForSaveAndLink || [],
      objectsForCheckAndUpdate: parsedPDFData.objectsForCheckAndUpdate || [],
      fileForDriveImport: fileForDriveImport || {},
      unitsForSave: pdfData.unitsForSave || [],
      nonResidentOwnersInDB: parsedPDFData.object?.nonResidentOwnersInDB || [],
      nonResidentOwnersNotInDB:
        parsedPDFData.object?.nonResidentOwnersNotInDB || [],
      residentOwnersInDB: parsedPDFData.object?.residentOwnersInDB || [],
      residentOwnersNotInDB: parsedPDFData.object?.residentOwnersNotInDB || [],
    });
  }, [parsedPDFData, pdfData, fileForDriveImport]);

  const onDrop = useCallback(
    (acceptedFiles: any) => {
      const formData = new FormData();

      const type = acceptedFiles[0].type.split("/").slice(-1)[0];

      if (type.toLowerCase() !== "pdf")
        return AlertService.warning("not correct file format");

      const fileName = acceptedFiles[0].name;
      formData.append("pdf", acceptedFiles[0], fileName);

      dispatch(sendPDFRequest(formData, navigate));
    },
    [dispatch, navigate]
  );

  const handleChange = (type: string, value: HandleChangeEvent): void => {
    if (type === "isFullList" && typeof value === "boolean") {
      if (value) {
        return setPdfState({
          ...pdfState,
          linkedObjects: [...pdfState.linkedObjects,],
          objectsForUnlink: parsedPDFData.objectsForUnlink,
          fullList: value,
        });
      }
      return setPdfState({
         ...pdfState,
         linkedObjects: parsedPDFData.linkedObjects,
         objectsForUnlink: [],
         fullList: value,
      });
    }
    if (type === "entity_type" && typeof value === "string") {
      if (!value) {
        return setPdfState({
          ...pdfState,
          noTypeWarning: true,
        });
      }
      return setPdfState({
         ...pdfState,
         entity: {
           ...pdfState.entity,
           entity_type: value,
         },
      });
    }
    if (type === "is_dead" && typeof value === "number") {
      const numericValue = Number(value);
      setPdfState({
        ...pdfState,
        isDead: numericValue,
        entity: {
          ...pdfState.entity,
          is_dead:
            numericValue === 1
              ? false
              : numericValue === 2
                ? true
                : null,
        },
      });
    }
    if (type === "lastImportDate" && (value === null || dayjs.isDayjs(value))) {
      return setPdfState({
        ...pdfState,
        lastImportDate: value,
      });
    }
  };

  useEffect(() => {
    if (pdfState.entity) {
      if (pdfState.entity.entity_type) {
        setNotType(false);
      }
    }
  }, [pdfState.entity]);

  const handleCancel = () => {
    dispatch(unlinkPDFRequest(fileForDriveImport));
    setPdfState(pdfInitialState);
  };

  const handleSave = () => {
    if (entity) {
      if (!!pdfState.entity.entity_type) {
        dispatch(
          savePDFRequest(
            {
              // object,
              entity,
              fileForDriveImport,
              fullList,
              lastImportDate,
              linkedObjects,
              objectsForLink,
              objectsForSaveAndLink,
              objectsForUnlink,
              unitsForSave,
            },
            navigate
          )
        );
      } else {
        setNotType(true);
        AlertService.warning("The entity type field must be filled in");
      }
    } else if (object) {
      dispatch(
        savePDFRequest(
          {
            object,
            fileForDriveImport,
            lastImportDate,
            // owners,
          },
          navigate
        )
      );
    }
  };

  const {
    object,
    entity,
    statistic,
    isDead,
    linkedObjects,
    objectsForUnlink,
    objectsForLink,
    objectsForSaveAndLink,
    objectsForCheckAndUpdate,
    fullList,
    lastImportDate,
    unitsForSave,
    nonResidentOwnersInDB,
    nonResidentOwnersNotInDB,
    residentOwnersInDB,
    residentOwnersNotInDB,
  } = pdfState || {};

  return (
    <Paper sx={styles.content}>
      {isFetching ? (
        <Box sx={styles.wrapperSpinner}>
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        <>
          {Object.keys(pdfState).length &&
          entity &&
          Object.keys(entity).length > 2 ? (
            <Box sx={styles.body}>
              <Head
                fullList={fullList}
                lastImportDate={lastImportDate}
                isDead={isDead}
                entity={entity}
                object={object}
                statistic={statistic}
                onChange={handleChange}
                noType={notType}
                setNoType={setNotType}
              />
              <Main
                objectsForUnlink={objectsForUnlink}
                objectsForLink={objectsForLink}
                linkedObjects={linkedObjects}
                objectsForSaveAndLink={objectsForSaveAndLink}
                objectsForCheckAndUpdate={objectsForCheckAndUpdate}
                type={modelTypes.ENTITY}
              />
              <Footer onCancel={handleCancel} handleSave={handleSave} />
            </Box>
          ) : Object.keys(pdfState).length &&
            object &&
            Object.keys(object).length ? (
            <Box sx={styles.body}>
              <Head
                fullList={fullList}
                lastImportDate={lastImportDate}
                object={object}
                statistic={statistic}
                onChange={handleChange}
              />
              <Main
                nonResidentOwnersInDB={nonResidentOwnersInDB}
                nonResidentOwnersNotInDB={nonResidentOwnersNotInDB}
                residentOwnersInDB={residentOwnersInDB}
                residentOwnersNotInDB={residentOwnersNotInDB}
                type={modelTypes.OBJECT}
              />
              <Footer onCancel={handleCancel} handleSave={handleSave} />
            </Box>
          ) : (
            <Dropzone onDrop={(acceptedFiles) => onDrop(acceptedFiles)}>
              {({ getRootProps, getInputProps, isDragActive }) => (
                <Box {...getRootProps()} sx={styles.contentHolder}>
                  <input {...getInputProps()} />
                  {isDragActive ? (
                    <Typography
                      sx={styles.dragWrapperIsActive}
                      variant="subtitle1"
                    >
                      Drop files here...
                    </Typography>
                  ) : (
                    <Typography sx={styles.wrapperText} variant="subtitle1">
                      Try dropping some files here, or click to select files to upload.
                    </Typography>
                  )}
                </Box>
              )}
            </Dropzone>
          )}
        </>
      )}
    </Paper>
  );
};

function mapStateToProps(state: RootState) {
  const {
    pdfData,
    objectsForCheckbox,
    isFetching,
    parsedPDFData,
    fileForDriveImport,
    savePDF,
  } = selectPDF(state);
  return {
    pdfData,
    objectsForCheckbox,
    isFetching,
    parsedPDFData,
    fileForDriveImport,
    savePDF,
  };
}

export default connect(mapStateToProps)(ImportPDF);
